// Canonical screen registry — every navigable v2 route the ⌘K palette can
// jump to. Superset of the sidebar's NAV_PRIMARY + NAV_MORE arrays plus the
// handful of direct routes that aren't surfaced in the sidebar (`/today`,
// `/settings`, `/settings/preferences`, `/ask-adva`; `/import` is already
// in NAV_MORE). A drift-guard test ensures every nav href is represented here.

import {
  Briefcase,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Contact2,
  DollarSign,
  FileText,
  Keyboard,
  LayoutDashboard,
  Map,
  MapPin,
  Package,
  Receipt,
  Settings,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Upload,
  UserCheck,
  Users,
  UsersRound,
} from "lucide-react";

type ScreenGroup = "primary" | "more" | "settings" | "ai";

export interface ScreenDef {
  /** Stable identifier (kebab-case route slug). */
  id: string;
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  group: ScreenGroup;
  /** Aliases used by cmdk fuzzy filter. */
  keywords?: readonly string[];
}

// The order of entries here is the order palette renders them in. Within each
// group, the order mirrors the sidebar arrays so the drift-guard test passes.
export const SCREENS: readonly ScreenDef[] = [
  // ── primary (NAV_PRIMARY order) ─────────────────────────────────────────
  {
    id: "today",
    label: "Today",
    href: "/today",
    icon: CalendarDays,
    group: "primary",
    keywords: ["today", "home"],
  },
  {
    id: "dashboard",
    label: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard,
    group: "primary",
    keywords: ["home", "overview"],
  },
  {
    id: "opportunities",
    label: "Opportunities",
    href: "/opportunities",
    icon: Target,
    group: "primary",
    keywords: ["pipeline", "deals"],
  },
  {
    id: "appointments",
    label: "Appointments",
    href: "/appointments",
    icon: CalendarCheck,
    group: "primary",
    keywords: ["calendar", "schedule"],
  },
  {
    id: "jobs",
    label: "Jobs",
    href: "/jobs",
    icon: Briefcase,
    group: "primary",
    keywords: ["work orders", "operations"],
  },
  {
    id: "invoices",
    label: "Invoices",
    href: "/invoices",
    icon: FileText,
    group: "primary",
    keywords: ["billing", "finance"],
  },
  {
    id: "transactions",
    label: "Transactions",
    href: "/transactions",
    icon: Receipt,
    group: "primary",
    keywords: ["payments", "ledger"],
  },
  {
    id: "customers",
    label: "Customers",
    href: "/customers",
    icon: Users,
    group: "primary",
    keywords: ["clients", "accounts"],
  },

  // ── more (NAV_MORE order) ───────────────────────────────────────────────
  {
    id: "contacts",
    label: "Contacts",
    href: "/contacts",
    icon: Contact2,
    group: "more",
    keywords: ["people", "directory"],
  },
  {
    id: "proposals",
    label: "Proposals",
    href: "/proposals",
    icon: ClipboardList,
    group: "more",
    keywords: ["quotes", "estimates"],
  },
  {
    id: "products",
    label: "Products",
    href: "/products",
    icon: Package,
    group: "more",
    keywords: ["catalog", "skus"],
  },
  {
    id: "locations",
    label: "Locations",
    href: "/locations",
    icon: MapPin,
    group: "more",
    keywords: ["addresses", "sites"],
  },
  {
    id: "crews",
    label: "Crews",
    href: "/crews",
    icon: UsersRound,
    group: "more",
    keywords: ["teams", "field"],
  },
  {
    id: "team-members",
    label: "Team Members",
    href: "/team-members",
    icon: UserCheck,
    group: "more",
    keywords: ["staff", "users"],
  },
  {
    id: "territories",
    label: "Territories",
    href: "/territories",
    icon: Map,
    group: "more",
    keywords: ["regions", "service area"],
  },
  {
    id: "warranties",
    label: "Warranties",
    href: "/warranties",
    icon: ShieldCheck,
    group: "more",
    keywords: ["claims", "coverage"],
  },
  {
    id: "service-agreements",
    label: "Service Agreements",
    href: "/service-agreements",
    icon: CalendarClock,
    group: "more",
    keywords: ["maintenance", "recurring"],
  },
  {
    id: "service-plans",
    label: "Service Plans",
    href: "/service-plans",
    icon: ClipboardCheck,
    group: "more",
    keywords: ["plans", "templates"],
  },
  {
    id: "commissions",
    label: "Commissions",
    href: "/commissions",
    icon: DollarSign,
    group: "more",
    keywords: ["payouts", "compensation"],
  },
  {
    id: "reports",
    label: "Reports",
    href: "/reports",
    icon: TrendingUp,
    group: "more",
    keywords: ["analytics", "metrics"],
  },
  {
    id: "imports",
    label: "Imports",
    href: "/import",
    icon: Upload,
    group: "more",
    keywords: ["upload", "data"],
  },

  // ── settings — surfaces not in the sidebar nav arrays ───────────────────
  {
    id: "settings",
    label: "Settings",
    href: "/settings",
    icon: Settings,
    group: "settings",
    keywords: ["preferences", "config"],
  },
  {
    id: "keyboard-shortcuts",
    label: "Keyboard Shortcuts",
    href: "/settings/preferences",
    icon: Keyboard,
    group: "settings",
    keywords: ["shortcuts", "keys", "bindings"],
  },

  // ── ai surface (placeholder route, real chat lands with ADV-803) ────────
  {
    id: "ask-adva",
    label: "Ask Adva",
    href: "/ask-adva",
    icon: Sparkles,
    group: "ai",
    keywords: ["ai", "chat", "assistant", "ava"],
  },
] as const;

/** Filter screens by group. Stable order matches registry order. */
export function screensByGroup(group: ScreenGroup): ScreenDef[] {
  return SCREENS.filter((s) => s.group === group);
}
